/**
 * Utility functions for processing citation tags in markdown content
 */

/**
 * Preprocesses cite tags to handle malformed cases:
 * - Removes cite tags around non-asset content (text that doesn't start with "asset_")
 * - Splits multiple comma-separated assets into individual cite tags
 * - Trims whitespace around asset IDs
 * - Removes empty cite tags
 *
 * @param md - The markdown string containing cite tags
 * @returns Processed markdown with cleaned up cite tags
 */
export const preprocessCiteTags = (md: string): string => {
  const citeRegex = /<cite>(.*?)<\/cite>/g;
  return md.replace(citeRegex, (_match, content) => {
    const trimmedContent = content.trim();

    // Case 1: Empty content - remove entirely
    if (!trimmedContent) {
      return "";
    }

    // Case 2: Content doesn't start with "asset_" - remove cite tags, keep content
    if (!trimmedContent.startsWith("asset_")) {
      return trimmedContent;
    }

    // Case 3: Multiple assets separated by commas - split into separate cite tags
    if (trimmedContent.includes(",")) {
      const assets = trimmedContent
        .split(",")
        .map((asset: string) => asset.trim())
        .filter((asset: string) => asset.startsWith("asset_"));
      return assets.map((asset: string) => `<cite>${asset}</cite>`).join("");
    }

    // Case 4: Single valid asset (possibly with whitespace) - return trimmed
    return `<cite>${trimmedContent}</cite>`;
  });
};

/**
 * Extracts all asset IDs from cite tags in markdown content
 *
 * @param md - The markdown string containing cite tags
 * @returns Array of unique asset IDs found in cite tags
 */
export const extractAssetIdsFromCites = (md: string): string[] => {
  const processedMd = preprocessCiteTags(md);
  const citeRegex = /<cite>\s*([^<]*?)\s*<\/cite>/g;
  const assetIds: string[] = [];

  for (const match of processedMd.matchAll(citeRegex) ?? []) {
    const assetId = match?.[1]?.trim();
    if (assetId?.startsWith("asset_") && !assetIds.includes(assetId)) {
      assetIds.push(assetId);
    }
  }

  return assetIds;
};

/**
 * Creates a map of asset IDs to display numbers based on their order of appearance
 *
 * @param md - The markdown string containing cite tags
 * @returns Map of asset ID to display number
 */
export const createAssetIdToDisplayNumberMap = (md: string): Map<string, number> => {
  const assetIds = extractAssetIdsFromCites(md);
  const map = new Map<string, number>();

  assetIds.forEach((assetId, index) => {
    map.set(assetId, index + 1);
  });

  return map;
};

/**
 * Validates if a string is a valid asset ID format
 *
 * @param str - The string to validate
 * @returns True if the string is a valid asset ID format
 */
export const isValidAssetId = (str: string): boolean => {
  return typeof str === "string" && str.trim().startsWith("asset_");
};
